import React, { useContext, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Box,
  Button,
  CssBaseline,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  HeadsetMicOutlined,
  SecurityOutlined,
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import logo from "../../assets/tu-enfermera-logo.png";
import { brand, brandGradient } from "../../styles/brand";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "grid",
    gridTemplateColumns: "minmax(420px, 1.08fr) minmax(420px, .92fr)",
    background: theme.palette.background.paper,
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: theme.spacing(2),
      background: brandGradient,
    },
  },
  brandPanel: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "8vw",
    color: "#FFFFFF",
    background: "linear-gradient(145deg, #0B4263 0%, #082F49 100%)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 520,
      height: 520,
      borderRadius: "50%",
      background: "rgba(255,255,255,.08)",
      right: -210,
      top: -230,
    },
    "&::after": {
      content: '""',
      position: "absolute",
      width: 380,
      height: 380,
      borderRadius: "50%",
      border: "1px solid rgba(255,255,255,.13)",
      left: -170,
      bottom: -210,
    },
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  logoCard: {
    position: "relative",
    width: "min(440px, 82%)",
    padding: theme.spacing(1.5, 2.5),
    borderRadius: 18,
    background: "#FFFFFF",
    boxShadow: "0 20px 45px rgba(3, 36, 58, .22)",
  },
  logo: {
    display: "block",
    width: "100%",
    height: 86,
    objectFit: "contain",
  },
  heroTitle: {
    position: "relative",
    maxWidth: 560,
    marginTop: theme.spacing(5),
    fontSize: "clamp(2.2rem, 4vw, 4.2rem)",
    lineHeight: 1.04,
    letterSpacing: "-.04em",
    fontWeight: 800,
  },
  heroText: {
    position: "relative",
    maxWidth: 520,
    marginTop: theme.spacing(2.5),
    color: "rgba(255,255,255,.83)",
    fontSize: "1.05rem",
    lineHeight: 1.75,
  },
  features: {
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(4),
  },
  feature: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.75),
    border: "1px solid rgba(255,255,255,.2)",
    borderRadius: 12,
    background: "rgba(255,255,255,.1)",
    backdropFilter: "blur(8px)",
  },
  formPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(5),
    background: theme.palette.type === "dark" ? "#071D2C" : brand.surface,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: 480,
      padding: 0,
      background: "transparent",
    },
  },
  loginCard: {
    width: "100%",
    maxWidth: 470,
    padding: theme.spacing(5),
    border: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,.08)" : "1px solid #E7EFF4",
    borderRadius: 25,
    boxShadow: "0 30px 70px rgba(0, 25, 40, .14)",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(3) },
  },
  mobileLogo: {
    display: "none",
    width: "100%",
    height: 70,
    objectFit: "contain",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: { display: "block" },
  },
  eyebrow: {
    color: brand.blue,
    fontWeight: 800,
    letterSpacing: ".12em",
    textTransform: "uppercase",
    fontSize: ".72rem",
  },
  title: { marginTop: theme.spacing(1), color: theme.palette.text.primary },
  subtitle: { marginTop: theme.spacing(1), marginBottom: theme.spacing(3), color: theme.palette.text.secondary },
  field: { marginBottom: theme.spacing(1) },
  submit: {
    height: 50,
    marginTop: theme.spacing(2),
    background: brandGradient,
    boxShadow: "0 12px 25px rgba(8, 135, 197, .25)",
    "&:hover": { background: `linear-gradient(135deg, ${brand.blue}, ${brand.navy})` },
  },
  footer: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid",
    borderColor: theme.palette.divider,
    textAlign: "center",
  },
}));

const Login = () => {
  const classes = useStyles();
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = (event) => {
    setUser({ ...user, [event.target.name]: event.target.value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin(user);
  };

  return (
    <main className={classes.root}>
      <CssBaseline />
      <section className={classes.brandPanel}>
        <div className={classes.logoCard}>
          <img src={logo} alt="Tu Enfermera" className={classes.logo} />
        </div>
        <Typography component="h1" className={classes.heroTitle}>
          Atención que conecta y cuida.
        </Typography>
        <Typography className={classes.heroText}>
          Gestiona conversaciones, pacientes y solicitudes desde un solo centro de atención seguro.
        </Typography>
        <div className={classes.features}>
          <div className={classes.feature}>
            <HeadsetMicOutlined />
            <span>Atención centralizada</span>
          </div>
          <div className={classes.feature}>
            <SecurityOutlined />
            <span>Acceso protegido</span>
          </div>
        </div>
      </section>

      <section className={classes.formPanel}>
        <Paper className={classes.loginCard} elevation={0}>
          <img src={logo} alt="Tu Enfermera" className={classes.mobileLogo} />
          <Typography className={classes.eyebrow}>Centro de atención</Typography>
          <Typography component="h2" variant="h4" className={classes.title}>
            Bienvenido
          </Typography>
          <Typography className={classes.subtitle}>
            Ingresa tus datos para continuar con tu jornada.
          </Typography>
          <form noValidate onSubmit={handleSubmit}>
            <TextField
              className={classes.field}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
            />
            <TextField
              className={classes.field}
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Mostrar u ocultar contraseña"
                      onClick={() => setShowPassword((value) => !value)}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
            >
              Iniciar sesión
            </Button>
          </form>
          <Box className={classes.footer}>
            <Grid container justify="center">
              <Grid item>
                <Typography variant="body2" color="textSecondary">
                  ¿Necesitas una cuenta?{" "}
                  <Link component={RouterLink} to="/signup" color="primary">
                    Solicitar acceso
                  </Link>
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </section>
    </main>
  );
};

export default Login;
